import { onlineMembers, presencePayload, PRESENCE_CHANNEL, type Member, type StudioPresence } from '@hive/shared';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

export type PresenceStatus = 'connecting' | 'live' | 'offline';

/**
 * Joins the private Realtime presence channel "studio" as `member` and returns who
 * is online (one entry per member, however many windows they have open).
 */
export function useStudioPresence(member: Pick<Member, 'id' | 'display_name' | 'avatar_url'>) {
  const [online, setOnline] = useState<StudioPresence[]>([]);
  const [status, setStatus] = useState<PresenceStatus>('connecting');
  const { id, display_name, avatar_url } = member;

  useEffect(() => {
    const client = supabase();
    let channel: RealtimeChannel | undefined;
    let cancelled = false;

    void (async () => {
      // Private channels authorise with the user's JWT (RLS on realtime.messages).
      await client.realtime.setAuth();
      if (cancelled) return;
      const ch = client.channel(PRESENCE_CHANNEL, { config: { private: true, presence: { key: id } } });
      channel = ch;
      ch.on('presence', { event: 'sync' }, () => {
        setOnline(onlineMembers(ch.presenceState<StudioPresence>()));
      }).subscribe((state) => {
        if (state === 'SUBSCRIBED') {
          setStatus('live');
          void ch.track(presencePayload({ id, display_name, avatar_url }));
        } else if (state === 'CHANNEL_ERROR' || state === 'TIMED_OUT' || state === 'CLOSED') {
          setStatus('offline');
        }
      });
    })();

    return () => {
      cancelled = true;
      if (channel) void client.removeChannel(channel);
    };
  }, [id, display_name, avatar_url]);

  return { online, status };
}
